import { createSignal, onMount, onCleanup, For, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { View, Text, hexColor } from "@lightningtv/solid";
import OnScreenKeyboard, { keyboardWidth } from "../registration/OnScreenKeyboard";
import Box from "../Box";

type FieldId = "username" | "password";

const FIELDS: { id: FieldId; label: string; hint: string }[] = [
  { id: "username", label: "Username", hint: "Enter your username" },
  { id: "password", label: "Password", hint: "Enter your password" },
];

// Layout, in canvas pixels: a popup centered on the 1920x1080 screen with
// the fields on the left and the on-screen keyboard on the right
const MODAL_W = 1600;
const MODAL_H = 580;
const MODAL_X = (1920 - MODAL_W) / 2;
const MODAL_Y = 250;
const PAD = 60;
const ROW_W = 560;
const ROW_H = 70;
const FIELD_Y0 = 150;
const FIELD_PITCH = 86;
const LOGIN_Y = FIELD_Y0 + FIELDS.length * FIELD_PITCH + 8;

const C = {
  row: "#1C1C20",
  rowFocused: "#2C2C33",
  rowBorder: "#3A3A42",
  muted: "#B9B9BE",
  error: "#FF8A80",
  white: "#FFFFFF",
};

type CredentialsLoginProps = {
  accent: string;
  // Throws an Error to show its message in the popup
  onLogin: (username: string, password: string) => Promise<void>;
  onClose: () => void;
};

// "Login with username and password" popup for the licence screen. Typed
// with the remote on the on-screen keyboard, or on a hardware keyboard.
const CredentialsLogin = (props: CredentialsLoginProps) => {
  const [values, setValues] = createStore<Record<FieldId, string>>({ username: "", password: "" });
  // Which element has focus, e.g. "field-username", "login"
  const [focused, setFocused] = createSignal<string | null>(null);
  // The field the on-screen keyboard is typing into
  const [editing, setEditing] = createSignal<FieldId | null>(null);
  const [error, setError] = createSignal("");
  const [loggingIn, setLoggingIn] = createSignal(false);

  const fieldRefs: Partial<Record<FieldId, any>> = {};
  let loginRef: any;
  const keyboardHandle: { focus?: () => void } = {};

  const focusedField = () => {
    const f = focused();
    return f?.startsWith("field-") ? (f.slice(6) as FieldId) : null;
  };

  const focusProps = (id: string) => ({
    onFocus: () => setFocused(id),
    onBlur: () => setFocused((f) => (f === id ? null : f)),
  });

  const addChar = (id: FieldId, c: string) => {
    setValues(id, (v) => (v + c).slice(0, 64));
    setError("");
  };
  const deleteChar = (id: FieldId) => {
    setValues(id, (v) => v.slice(0, -1));
    setError("");
  };

  const startEditing = (id: FieldId) => {
    setEditing(id);
    keyboardHandle.focus?.();
  };
  const stopEditing = () => {
    const id = editing();
    setEditing(null);
    if (id) fieldRefs[id]?.setFocus();
  };
  // Keyboard "OK": username -> password -> Login button
  const nextField = () => {
    const idx = FIELDS.findIndex((f) => f.id === editing());
    const next = FIELDS[idx + 1];
    if (next) {
      startEditing(next.id);
    } else {
      setEditing(null);
      loginRef?.setFocus();
    }
  };

  const login = async () => {
    if (loggingIn()) return;
    const username = values.username.trim();
    if (!username) {
      setError("Please enter your username.");
      fieldRefs.username?.setFocus();
      return;
    }
    setLoggingIn(true);
    setError("");
    try {
      await props.onLogin(username, values.password);
    } catch (e: any) {
      setError(e?.message || "Login failed. Please try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const close = () => { props.onClose(); return true; };

  onMount(() => {
    setTimeout(() => fieldRefs.username?.setFocus(), 0);

    // A hardware keyboard, or the remote's number keys, types into the
    // field being edited, or into the highlighted row
    const onKey = (e: KeyboardEvent) => {
      const id = editing() ?? focusedField();
      if (!id) return;
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) addChar(id, e.key);
    };
    document.addEventListener("keydown", onKey);
    onCleanup(() => document.removeEventListener("keydown", onKey));
  });

  // What a field shows: the password as dots, plus a cursor while typing
  const display = (id: FieldId) => {
    const v = values[id];
    const shown = id === "password" ? "*".repeat(v.length) : v;
    return editing() === id ? shown + "|" : shown;
  };

  return (
    <>
      {/* Dimmed backdrop */}
      <View width={1920} height={1080} color={hexColor("#000000")} alpha={0.8} />

      <Box x={MODAL_X} y={MODAL_Y} width={MODAL_W} height={MODAL_H} radius={30} fill="#111114" border="#FFFFFF33">
        <Text x={PAD} y={44} style={{ fontSize: 36, fontWeight: "bold", color: hexColor(C.white) }}>
          Login
        </Text>
        <Text x={PAD} y={94} style={{ fontSize: 20, color: hexColor(C.muted) }}>
          Enter your username and password
        </Text>

        <For each={FIELDS}>
          {(field, i) => {
            const isFocused = () => focused() === `field-${field.id}`;
            const isEditing = () => editing() === field.id;
            return (
              <Box x={PAD} y={FIELD_Y0 + i() * FIELD_PITCH} width={ROW_W} height={ROW_H} radius={14}
                fill={isFocused() || isEditing() ? C.rowFocused : C.row}
                border={isEditing() ? props.accent : isFocused() ? C.white : C.rowBorder}>
                <View ref={(el: any) => { fieldRefs[field.id] = el; }} width={ROW_W} height={ROW_H}
                  {...focusProps(`field-${field.id}`)}
                  onEnter={() => { startEditing(field.id); return true; }}
                  onRight={() => { startEditing(field.id); return true; }}
                  onLeft={() => true}
                  onUp={() => { fieldRefs[FIELDS[i() - 1]?.id]?.setFocus(); return true; }}
                  onDown={() => {
                    const next = FIELDS[i() + 1];
                    if (next) fieldRefs[next.id]?.setFocus();
                    else loginRef?.setFocus();
                    return true;
                  }}
                  onBackspace={() => { deleteChar(field.id); return true; }}
                  onEscape={close}>
                  <Text x={22} y={9} style={{ fontSize: 16 }} color={hexColor(C.muted)}>
                    {field.label}
                  </Text>
                  <Text x={22} y={32} contain="width" style={{ width: ROW_W - 44, maxLines: 1, fontSize: 26 }}
                    color={hexColor(values[field.id] || isEditing() ? C.white : "#6E6E76")}>
                    {display(field.id) || field.hint}
                  </Text>
                </View>
              </Box>
            );
          }}
        </For>

        <Box x={PAD} y={LOGIN_Y} width={ROW_W} height={64} radius={32}
          fill={focused() === "login" ? C.white : props.accent}
          border={focused() === "login" ? C.white : props.accent}>
          <View ref={(el: any) => { loginRef = el; }} width={ROW_W} height={64}
            {...focusProps("login")}
            onEnter={() => { login(); return true; }}
            onUp={() => { fieldRefs[FIELDS[FIELDS.length - 1].id]?.setFocus(); return true; }}
            onDown={() => true}
            onLeft={() => true}
            onRight={() => true}
            onEscape={close}>
            <Text y={19} contain="width" textAlign="center" style={{ width: ROW_W, fontSize: 26, fontWeight: "bold" }}
              color={hexColor(focused() === "login" ? "#000000" : C.white)}>
              {loggingIn() ? "Logging in..." : "Login"}
            </Text>
          </View>
        </Box>

        <Show when={error()}>
          <Text x={PAD} y={LOGIN_Y + 84} contain="width"
            style={{ width: ROW_W, maxLines: 2, fontSize: 20, color: hexColor(C.error) }}>
            {error()}
          </Text>
        </Show>

        <Text x={PAD} y={MODAL_H - 50} style={{ fontSize: 18, color: hexColor(C.muted) }}>
          {editing() ? "OK: next   |   Back: stop typing" : "Press OK on a field to type   |   Back: close"}
        </Text>

        {/* Dimmed until a field is being edited */}
        <View x={MODAL_W - PAD - keyboardWidth("text")} y={FIELD_Y0} alpha={editing() ? 1 : 0.4}>
          <OnScreenKeyboard
            mode="text"
            accent={props.accent}
            keyboardRef={keyboardHandle}
            onChar={(c) => { const id = editing(); if (id) addChar(id, c); }}
            onDelete={() => { const id = editing(); if (id) deleteChar(id); }}
            onDone={nextField}
            onExit={stopEditing}
          />
        </View>
      </Box>
    </>
  );
};

export default CredentialsLogin;
